import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

const MAX_CHUNK_LENGTH = 490;

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  constructor(private http: HttpClient) {}

  translateToArabic(text: string): Observable<string> {
    if (!text || text.trim() === '') {
      return of('');
    }
    return this.translateChunked(text, 'en|ar');
  }

  translateToEnglish(text: string): Observable<string> {
    if (!text || text.trim() === '') {
      return of('');
    }
    return this.translateChunked(text, 'ar|en');
  }

  private translateChunked(text: string, langpair: string): Observable<string> {
    const chunks = this.splitIntoChunks(text);
    if (chunks.length === 1) {
      return this.translateSingle(chunks[0], langpair, text);
    }
    return forkJoin(chunks.map(chunk => this.translateSingle(chunk, langpair, chunk))).pipe(
      map(results => results.join(' '))
    );
  }

  private translateSingle(chunk: string, langpair: string, fallback: string): Observable<string> {
    const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(chunk)}&langpair=${langpair}`;
    return this.http.get<any>(url).pipe(
      map(response => {
        if (response?.responseData?.translatedText) {
          return response.responseData.translatedText;
        }
        return fallback;
      }),
      catchError(error => {
        console.error('Translation error:', error);
        return of(fallback);
      })
    );
  }

  private splitIntoChunks(text: string): string[] {
    const words = text.split(' ');
    const chunks: string[] = [];
    let current = '';

    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (candidate.length > MAX_CHUNK_LENGTH) {
        if (current) chunks.push(current);
        // Word itself exceeds limit — hard-split it
        if (word.length > MAX_CHUNK_LENGTH) {
          for (let i = 0; i < word.length; i += MAX_CHUNK_LENGTH) {
            chunks.push(word.slice(i, i + MAX_CHUNK_LENGTH));
          }
          current = '';
        } else {
          current = word;
        }
      } else {
        current = candidate;
      }
    }

    if (current) chunks.push(current);
    return chunks;
  }
}

